import SEO from '../components/SEO'
import DownloadCTA from '../components/sections/DownloadCTA'

const testedPlatforms = [
  { name: 'Zoom', type: 'Video', audio: true, screen: true, stealth: true },
  { name: 'Google Meet', type: 'Video', audio: true, screen: true, stealth: true },
  { name: 'Microsoft Teams', type: 'Video', audio: true, screen: true, stealth: true },
  { name: 'WebEx', type: 'Video', audio: true, screen: true, stealth: true },
  { name: 'HireVue', type: 'Assessment', audio: true, screen: true, stealth: true },
  { name: 'CodeSignal', type: 'Assessment', audio: true, screen: true, stealth: true },
  { name: 'HackerRank', type: 'Assessment', audio: true, screen: true, stealth: true },
  { name: 'CoderPad', type: 'Assessment', audio: true, screen: true, stealth: true },
  { name: 'Phone Call', type: 'Phone', audio: true, screen: false, stealth: true },
]

const stealthFeatures = [
  {
    title: 'Content-Protected Window',
    description: 'Intervio\'s window is invisible to all screen recording, screen sharing, and screen capture APIs. When you share your screen on Zoom, Teams, or Meet — Intervio simply doesn\'t appear.',
    icon: '🛡️',
    status: 'Active',
  },
  {
    title: 'Camouflage Mode',
    description: 'Rename the window title and icon to look like Notes, Calculator, Spotify, or any app you choose. Even if someone sees your taskbar, they\'ll see a familiar app name.',
    icon: '🎭',
    status: 'Active',
  },
  {
    title: 'Taskbar / Dock Hiding',
    description: 'Hide Intervio from Alt-Tab (Windows), Cmd-Tab (macOS), and the taskbar/dock entirely. The app is running but invisible to the OS task switcher.',
    icon: '👻',
    status: 'Active',
  },
  {
    title: 'Transparent Overlay',
    description: 'A fully transparent overlay mode that sits on top of your video call. Only the hint text is visible — the background shows through. No window switching needed.',
    icon: '🪟',
    status: 'Active',
  },
  {
    title: 'Local Processing',
    description: 'Your audio and screen content are processed locally on your device using Whisper AI and PaddleOCR. Nothing leaves your computer unless you explicitly enable cloud mode.',
    icon: '🔒',
    status: 'Always On',
  },
]

function StealthPage() {
  return (
    <>
      <SEO
        title="Is Intervio Safe? | Stealth Features"
        description="Intervio is undetectable during screen sharing. Content-protected window, camouflage mode, taskbar hiding, and local processing keep you safe."
        path="/stealth"
      />

      <section style={{ padding: '80px 0 40px', textAlign: 'center' }}>
        <div className="container">
          <h1 style={{ fontSize: '2.5rem', fontWeight: 800, marginBottom: 16 }}>
            Is Intervio Safe to Use?
          </h1>
          <p style={{ fontSize: '1.125rem', color: 'var(--text-secondary, #8b8fa4)', maxWidth: 640, margin: '0 auto' }}>
            Yes. Intervio is designed from the ground up to be undetectable during live interviews.
            Here's exactly how it works.
          </p>
        </div>
      </section>

      {/* Stealth Features */}
      <section style={{ padding: '40px 0' }}>
        <div className="container" style={{ maxWidth: 800, margin: '0 auto' }}>
          <h2 style={{ fontSize: '1.5rem', fontWeight: 700, marginBottom: 24, textAlign: 'center' }}>
            Stealth Features
          </h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {stealthFeatures.map((feature) => (
              <div
                key={feature.title}
                style={{
                  padding: '20px 24px',
                  background: 'var(--bg-card, rgba(26, 29, 43, 0.72))',
                  border: '1px solid var(--border-color, rgba(42, 46, 62, 0.6))',
                  borderRadius: 12,
                  display: 'flex',
                  gap: 16,
                  alignItems: 'flex-start',
                }}
              >
                <span style={{ fontSize: 28, flexShrink: 0 }}>{feature.icon}</span>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                    <h3 style={{ fontSize: '1rem', fontWeight: 600, margin: 0 }}>{feature.title}</h3>
                    <span style={{
                      fontSize: 11,
                      fontWeight: 600,
                      padding: '2px 8px',
                      borderRadius: 4,
                      background: 'rgba(52, 211, 153, 0.1)',
                      color: '#34d399',
                    }}>
                      {feature.status}
                    </span>
                  </div>
                  <p style={{ margin: 0, fontSize: 14, color: 'var(--text-secondary, #8b8fa4)', lineHeight: 1.6 }}>
                    {feature.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Tested Platforms */}
      <section style={{ padding: '40px 0' }}>
        <div className="container" style={{ maxWidth: 800, margin: '0 auto' }}>
          <h2 style={{ fontSize: '1.5rem', fontWeight: 700, marginBottom: 24, textAlign: 'center' }}>
            Tested Platforms
          </h2>
          <div style={{
            border: '1px solid var(--border-color, rgba(42, 46, 62, 0.6))',
            borderRadius: 12,
            overflow: 'hidden',
          }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
              <thead>
                <tr style={{ background: 'var(--bg-secondary, rgba(22, 25, 34, 0.7))' }}>
                  <th style={{ padding: '10px 16px', textAlign: 'left', fontWeight: 600, fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-secondary, #8b8fa4)' }}>Platform</th>
                  <th style={{ padding: '10px 16px', textAlign: 'center', fontWeight: 600, fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-secondary, #8b8fa4)' }}>Type</th>
                  <th style={{ padding: '10px 16px', textAlign: 'center', fontWeight: 600, fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-secondary, #8b8fa4)' }}>Audio</th>
                  <th style={{ padding: '10px 16px', textAlign: 'center', fontWeight: 600, fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-secondary, #8b8fa4)' }}>Screen</th>
                  <th style={{ padding: '10px 16px', textAlign: 'center', fontWeight: 600, fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-secondary, #8b8fa4)' }}>Stealth</th>
                </tr>
              </thead>
              <tbody>
                {testedPlatforms.map((p, i) => (
                  <tr key={p.name} style={{ borderTop: '1px solid var(--border-color, rgba(42, 46, 62, 0.6))', background: i % 2 === 0 ? 'transparent' : 'var(--bg-tertiary, rgba(28, 31, 46, 0.15))' }}>
                    <td style={{ padding: '10px 16px', fontWeight: 500 }}>{p.name}</td>
                    <td style={{ padding: '10px 16px', textAlign: 'center', color: 'var(--text-secondary, #8b8fa4)' }}>{p.type}</td>
                    <td style={{ padding: '10px 16px', textAlign: 'center' }}>{p.audio ? '✅' : '—'}</td>
                    <td style={{ padding: '10px 16px', textAlign: 'center' }}>{p.screen ? '✅' : '—'}</td>
                    <td style={{ padding: '10px 16px', textAlign: 'center' }}>{p.stealth ? '✅' : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p style={{ marginTop: 12, fontSize: 12, color: 'var(--text-muted, #5c6078)', textAlign: 'center' }}>
            ⚠️ Linux: Screen-share protection is not available on Linux due to OS limitations. Use "Share a browser tab" mode instead.
          </p>
        </div>
      </section>

      {/* Privacy */}
      <section style={{ padding: '40px 0 60px' }}>
        <div className="container" style={{ maxWidth: 800, margin: '0 auto' }}>
          <h2 style={{ fontSize: '1.5rem', fontWeight: 700, marginBottom: 24, textAlign: 'center' }}>
            Privacy Commitment
          </h2>
          <div style={{
            padding: '24px',
            background: 'var(--bg-card, rgba(26, 29, 43, 0.72))',
            border: '1px solid var(--border-color, rgba(42, 46, 62, 0.6))',
            borderRadius: 12,
            textAlign: 'center',
          }}>
            <p style={{ fontSize: 16, fontWeight: 600, marginBottom: 12 }}>
              🔒 Your audio and screen content never leave your device.
            </p>
            <p style={{ fontSize: 14, color: 'var(--text-secondary, #8b8fa4)', lineHeight: 1.6, maxWidth: 600, margin: '0 auto' }}>
              Intervio uses local AI models (Whisper for speech, PaddleOCR for screen text) that run entirely on your computer.
              No audio recordings, no screenshots, and no personal data are sent to any server unless you explicitly enable cloud processing mode.
              Even in cloud mode, audio is streamed for real-time transcription only — never stored.
            </p>
          </div>
        </div>
      </section>

      <DownloadCTA />
    </>
  )
}

export default StealthPage
